// apiService.js
import axios from 'axios';
import { API_CONFIG, ENDPOINTS } from '../config/api';
import { toast } from 'react-toastify';

class ApiService {
  constructor() {
    this.instance = axios.create({
      baseURL: API_CONFIG.BASE_URL,
      timeout: API_CONFIG.TIMEOUT,
      withCredentials: false,  // No cookies needed for header-based auth
      headers: {
        'Accept': 'application/json, text/plain, */*'
        // Don't set default Content-Type - let axios handle it based on data type
      }
    });

    // Response interceptor - Handle errors
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const { response } = error;
        let errorMessage = 'An unexpected error occurred';

        if (response) {
          switch (response.status) {
            case 400:
              errorMessage = this.handleBadRequest(response);
              break;
            case 401:
              // Check if this is invalid credentials, account not verified, or session expiration
              const errorCode401 = response.data?.error_code || response.data?.error?.code;
              const isInvalidCreds = errorCode401 === 'INVALID_CREDENTIALS' || errorCode401 === 'USER_FETCH_FAILED';
              const isAccountNotVerified = errorCode401 === 'ACCOUNT_NOT_VERIFIED';

              if (isInvalidCreds) {
                // Invalid credentials during login - use backend message
                if (response.data?.error?.message) {
                  errorMessage = response.data.error.message;
                } else if (response.data?.message) {
                  errorMessage = response.data.message;
                } else {
                  errorMessage = 'Invalid email or password';
                }
              } else if (isAccountNotVerified) {
                // Account not verified - don't show toast, let LoginPopup handle it
                errorMessage = response.data?.error?.message || response.data?.message || 'Account not verified';
              } else {
                // Session expiration - clear tokens and dispatch event
                errorMessage = 'Your session has expired. Please log in again.';
                this.clearTokens(); // Clear all tokens
                // Dispatch custom event for auth expiration
                window.dispatchEvent(new CustomEvent('authExpired', { detail: { message: errorMessage } }));
              }
              break;
            case 403:
              errorMessage = 'Insufficient permissions or access denied';
              break;
            case 404:
              errorMessage = 'The requested resource was not found';
              break;
            case 429:
              errorMessage = 'Too many requests. Please try again later.';
              break;
            case 500:
            case 502:
            case 503:
            case 504:
              // For 5xx errors, try to get specific message from backend
              if (response.data?.error?.message) {
                errorMessage = response.data.error.message;
              } else if (response.data?.message) {
                errorMessage = response.data.message;
              } else {
                errorMessage = 'An error occurred. Please try again later.';
              }
              break;
            default:
              // Use backend message if available
              // Handle both direct message and nested error object
              if (response.data?.error?.message) {
                errorMessage = response.data.error.message;
              } else if (response.data?.message) {
                errorMessage = response.data.message;
              } else if (response.data?.detail) {
                // Handle FastAPI validation errors (array of objects)
                if (Array.isArray(response.data.detail)) {
                  errorMessage = response.data.detail
                    .map(err => err.msg || JSON.stringify(err))
                    .join(', ');
                } else if (typeof response.data.detail === 'string') {
                  errorMessage = response.data.detail;
                } else {
                  errorMessage = JSON.stringify(response.data.detail);
                }
              }
          }
        } else if (error.request) {
          errorMessage = 'No response from server. Please check your connection.';
        } else {
          errorMessage = error.message || errorMessage;
        }

        // Determine if this is a session expiration, invalid credentials, or account not verified
        const errorCode = response?.data?.error_code || response?.data?.error?.code;
        const isInvalidCredentials = errorCode === 'INVALID_CREDENTIALS' || errorCode === 'USER_FETCH_FAILED';
        const isAccountNotVerified = errorCode === 'ACCOUNT_NOT_VERIFIED';
        const isSessionExpired = response?.status === 401 && !isInvalidCredentials && !isAccountNotVerified;

        // Show error toast for all errors except session expiration and account not verified (which are handled separately)
        if (!error.config?.skipErrorHandler && !isSessionExpired && !isAccountNotVerified) {
          toast.error(errorMessage);
        }

        // Create error object with sanitized data
        const errorObj = {
          message: errorMessage,
          status: response?.status,
          isAuthExpired: isSessionExpired,
          error_code: errorCode,
        };

        // Only include raw data in development mode
        if (process.env.NODE_ENV === 'development') {
          errorObj.data = response?.data;
          errorObj.originalError = error;
        }

        return Promise.reject(errorObj);
      }
    );

    // Request interceptor - add Authorization header with JWT token
    this.instance.interceptors.request.use(
      (config) => {
        // Add Authorization token if available
        const accessToken = this.getAccessToken();
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  // Token management methods
  getAccessToken() {
    return localStorage.getItem('access_token');
  }

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  }

  setTokens(accessToken, refreshToken) {
    if (accessToken) {
      localStorage.setItem('access_token', accessToken);
      if (process.env.NODE_ENV === 'development') {
        //console.log('✅ Access token stored');
      }
    }
    if (refreshToken) {
      localStorage.setItem('refresh_token', refreshToken);
      if (process.env.NODE_ENV === 'development') {
        //console.log('✅ Refresh token stored');
      }
    }
  }

  clearTokens() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token'); // Legacy token key
    if (process.env.NODE_ENV === 'development') {
      //console.log('🧹 All tokens cleared');
    }
  }

  // Auto-refresh token if expired
  async refreshAccessToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      if (process.env.NODE_ENV === 'development') {
        //console.log('🔄 Refreshing access token...');
      }
      const response = await this.post(`${ENDPOINTS.AUTH}/refresh-token`, {
        refresh_token: refreshToken
      }, {
        skipErrorHandler: true  // Don't show error toast for token refresh
      });

      if (response.access_token) {
        this.setTokens(response.access_token, null); // Only update access token
        if (process.env.NODE_ENV === 'development') {
          //console.log('✅ Access token refreshed successfully');
        }
        return response.access_token;
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('❌ Failed to refresh token:', error);
      }
      this.clearTokens();
      window.dispatchEvent(new CustomEvent('authExpired', {
        detail: { message: 'Session expired. Please log in again.' }
      }));
      throw error;
    }
  }

  // Helper methods
  handleBadRequest(response) {
    // Handle FastAPI validation errors
    if (response.data?.detail && Array.isArray(response.data.detail)) {
      return response.data.detail
        .map(err => {
          const field = err.loc ? err.loc.join('.') : 'field';
          return `${field}: ${err.msg}`;
        })
        .join(', ');
    }

    // Handle other error formats
    if (response.data?.errors) {
      return Object.values(response.data.errors).flat().join(', ');
    }

    return response.data?.message || response.data?.detail || 'Invalid request';
  }

  // Core request method with improved logging
  async request(config) {
    if (process.env.NODE_ENV === 'development') {
      //console.log("===================");
      //console.log("=== API REQUEST ===");
      //console.log("Method:", config.method?.toUpperCase());
      //console.log("URL:", this.instance.defaults.baseURL + config.url);
      //console.log("Auth Token Available:", !!this.getAccessToken());

      if (config.data instanceof FormData) {
        // FormData logging disabled
      } else if (config.data instanceof URLSearchParams) {
        //console.log("Data: [URLSearchParams]");
        //console.log("  " + config.data.toString());
      } else {
        //console.log("Data:", config.data || {});
      }
      //console.log("===================");
    }

    const response = await this.instance(config);

    // Global success toasts for mutating requests (opt-out via config.suppressSuccessToast)
    const method = config.method?.toUpperCase();
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method) && !config.suppressSuccessToast) {
      let successMessage = 'Success';
      if (method === 'POST') successMessage = 'Created successfully';
      if (method === 'PUT' || method === 'PATCH') successMessage = 'Updated successfully';
      if (method === 'DELETE') successMessage = 'Deleted successfully';
      // Prefer backend message if present
      const serverMsg = response.data?.message || response.data?.detail;
      toast.success(serverMsg || successMessage);
    }

    return response.data;
  }

  // Payment methods
  createRegistrationPayment = async (registrationId, paymentData) => {
    return this.post(`${ENDPOINTS.PAYMENTS}/registration/${registrationId}`, paymentData);
  };

  updatePaymentStatus = async (orderId, paymentDetails) => {
    return this.post(`${ENDPOINTS.PAYMENTS}/update/${orderId}`, paymentDetails);
  };

  verifyPayment = async (paymentResponse) => {
    return this.post(`${ENDPOINTS.PAYMENTS}/verify`, paymentResponse);
  };

  verifyRegistrationPayment = async (registrationId, paymentResponse) => {
    return this.post(`${ENDPOINTS.PAYMENTS}/verify-registration/${registrationId}`, paymentResponse);
  };

  // HTTP method shortcuts
  get(url, config = {}) {
    return this.request({ method: 'get', url, ...config });
  }

  post(url, data, config = {}) {
    return this.request({ method: 'post', url, data, ...config });
  }

  put(url, data, config = {}) {
    return this.request({ method: 'put', url, data, ...config });
  }

  delete(url, config = {}) {
    return this.request({ method: 'delete', url, ...config });
  }

  patch(url, data, config = {}) {
    return this.request({ method: 'patch', url, data, ...config });
  }

  // Updated login method - stores JWT tokens from response
  login = async (email, password) => {
    const formData = new URLSearchParams();
    formData.append("username", email);
    formData.append("password", password);

    if (process.env.NODE_ENV === 'development') {
      //console.log("Login payload:", formData.toString());
    }

    // Axios automatically sets Content-Type to application/x-www-form-urlencoded for URLSearchParams
    const response = await this.post(`${ENDPOINTS.AUTH}/login`, formData);

    // Store JWT tokens from login response
    if (response.access_token && response.refresh_token) {
      this.setTokens(response.access_token, response.refresh_token);
      if (process.env.NODE_ENV === 'development') {
        //console.log('✅ JWT tokens stored from login response');
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn('⚠️ No JWT tokens found in login response');
      }
    }

    return response;
  };

  // Updated logout method - clears JWT tokens
  logout = async () => {
    try {
      const response = await this.post(`${ENDPOINTS.AUTH}/logout`, {}, { suppressSuccessToast: true });
      this.clearTokens(); // Clear all stored tokens
      return response;
    } catch (error) {
      this.clearTokens(); // Clear tokens even if logout fails
      throw error;
    }
  };

  // Rest of your API methods remain the same...
  register = (userData) => {
    return this.post(`${ENDPOINTS.AUTH}/register`, userData);
  };

  getMe = (config = {}) => {
    return this.get(`${ENDPOINTS.AUTH}/me`, config);
  };

  forgotPassword = (email) => {
    return this.post('/auth/forgot-password', { email });
  };

  verifyOtp = (email, otp, newPassword) => {
    return this.post('/auth/verify-otp', { email, otp, new_password: newPassword });
  };

  resetPasswordWithCurrent = (currentPassword, newPassword) => {
    return this.post('/auth/reset-password', { current_password: currentPassword, new_password: newPassword });
  };

  checkEmailExists = (email) => {
    return this.post('/auth/check-email-exists', { email });
  };

  // User Profile API
  getUserProfile = () => {
    return this.get(`${ENDPOINTS.USERS}profile`);
  };

  updateUserProfile = (userData) => {
    return this.put(`${ENDPOINTS.USERS}profile`, userData);
  };

  uploadProfileImageAndUpdateProfile = (formData) => {
    return this.put(`${ENDPOINTS.USERS}profile`, formData);
  };

  // Address APIs
  addAddress = (addressData) => {
    return this.post(`${ENDPOINTS.USERS}profile/addresses`, addressData);
  };

  updateAddress = (addressData) => {
    return this.put(`${ENDPOINTS.USERS}profile/addresses/${addressData.id}`, addressData);
  };

  deleteAddress = (addressId) => {
    return this.delete(`${ENDPOINTS.USERS}profile/addresses/${addressId}`);
  };

  setDefaultAddress = (addressId) => {
    return this.put(`${ENDPOINTS.USERS}profile/addresses/${addressId}/default`);
  };

  // Classes API
  getClasses = (params = {}) => {
    return this.get(ENDPOINTS.CLASSES, { params });
  };

  getFeaturedClasses = (limit = 5) => {
    return this.get(`${ENDPOINTS.CLASSES}featured/list`, { params: { limit } });
  };

  getClassesByLevel = (level, params = {}) => {
    return this.get(`${ENDPOINTS.CLASSES}level/${level}`, { params });
  };

  getClassById = (classId) => {
    return this.get(`${ENDPOINTS.CLASSES}${classId}`);
  };

  createClass = (classData) => {
    return this.post(ENDPOINTS.CLASSES, classData);
  };

  updateClass = (classId, classData) => {
    return this.put(`${ENDPOINTS.CLASSES}${classId}`, classData);
  };

  deleteClass = (classId) => {
    return this.delete(`${ENDPOINTS.CLASSES}${classId}`);
  };

  // Events API
  getEvents = (params = {}) => {
    return this.get(ENDPOINTS.EVENTS, { params });
  };

  getFeaturedEvents = (limit = 5) => {
    return this.get(`${ENDPOINTS.EVENTS}featured/list`, { params: { limit } });
  };

  getEventById = (eventId) => {
    return this.get(`${ENDPOINTS.EVENTS}${eventId}`);
  };

  createEvent = (eventData) => {
    return this.post(ENDPOINTS.EVENTS, eventData);
  };

  updateEvent = (eventId, eventData) => {
    return this.put(`${ENDPOINTS.EVENTS}${eventId}`, eventData);
  };

  deleteEvent = (eventId) => {
    return this.delete(`${ENDPOINTS.EVENTS}${eventId}`);
  };

  // Blog API
  getBlogPosts = (params = {}) => {
    return this.get(`${ENDPOINTS.BLOG}posts`, { params });
  };

  getAllBlogPosts = (params = {}) => {
    return this.get(`${ENDPOINTS.BLOG}posts/all`, { params });
  };

  getFeaturedPosts = (limit = 5) => {
    return this.get(`${ENDPOINTS.BLOG}posts/featured`, { params: { limit } });
  };

  getBlogPostById = (postId) => {
    return this.get(`${ENDPOINTS.BLOG}posts/${postId}`);
  };

  searchPosts = (query, params = {}) => {
    return this.get(`${ENDPOINTS.BLOG}search`, { params: { q: query, ...params } });
  };

  toggleLike = (postId) => {
    return this.post(`${ENDPOINTS.BLOG}posts/${postId}/like`);
  };

  // Category Management
  getCategories = () => {
    return this.get(`${ENDPOINTS.BLOG}categories`);
  };

  getPostsByCategory = (categoryId, params = {}) => {
    return this.get(`${ENDPOINTS.BLOG}categories/${categoryId}/posts`, { params });
  };

  createCategory = (categoryData) => {
    return this.post(`${ENDPOINTS.BLOG}categories`, categoryData);
  };

  getCategoriesByNames = (categoryNames) => {
    return this.get(`${ENDPOINTS.BLOG}categories/by-names`, { params: { names: categoryNames.join(',') } });
  };

  createBlogPost = (postData) => {
    return this.post(`${ENDPOINTS.BLOG}posts`, postData);
  };

  createBlogPostWithCategories = (postData) => {
    return this.post(`${ENDPOINTS.BLOG}posts/with-categories`, postData);
  };

  updateBlogPost = (postId, postData) => {
    return this.put(`${ENDPOINTS.BLOG}posts/${postId}`, postData);
  };

  deleteBlogPost = (postId) => {
    return this.delete(`${ENDPOINTS.BLOG}posts/${postId}`);
  };

  // Store API
  getStoreItems = (params = {}) => {
    return this.get(ENDPOINTS.STORE, { params });
  };

  getStoreItemById = (itemId) => {
    return this.get(`${ENDPOINTS.STORE}${itemId}`);
  };

  createStoreItem = (itemData) => {
    return this.post(ENDPOINTS.STORE, itemData);
  };

  updateStoreItem = (itemId, itemData) => {
    return this.put(`${ENDPOINTS.STORE}${itemId}`, itemData);
  };

  deleteStoreItem = (itemId) => {
    return this.delete(`${ENDPOINTS.STORE}${itemId}`);
  };

  fetchStoreProducts = (params = {}) => {
    return this.get(ENDPOINTS.STORE + 'products', { params });
  };

  // Orders API
  getUserOrders = () => {
    return this.get(`${ENDPOINTS.ORDERS}/user/`);
  };
  getAllOrders = () => {
    return this.get(`${ENDPOINTS.ORDERS}/all`);
  };

  getOrderById = (orderId) => {
    return this.get(`${ENDPOINTS.ORDERS}/${orderId}`);
  };

  cancelOrder = (orderId) => {
    return this.patch(`${ENDPOINTS.ORDERS}/${orderId}/cancel`);
  };

  updateOrderStatus = (orderId, status) => {
    return this.patch(`${ENDPOINTS.ORDERS}/${orderId}/status`, { status });
  };

  // Registration management
  cancelRegistration = (registrationId) => {
    return this.patch(`${ENDPOINTS.REGISTRATIONS}${registrationId}/cancel`);
  };

  // Cart API
  getCart = () => {
    return this.get(ENDPOINTS.CART);
  };

  addToCart = (productId, quantity) => {
    return this.post(`${ENDPOINTS.CART}add`, { product_id: productId, quantity });
  };

  removeFromCart = (itemId) => {
    return this.delete(`${ENDPOINTS.CART}item/${itemId}`);
  };

  updateCartItem = (itemId, quantity) => {
    return this.put(`${ENDPOINTS.CART}item/${itemId}`, { quantity: quantity });
  };

  clearCart = () => {
    return this.post(`${ENDPOINTS.CART}clear`);
  };

  // Testimonials API
  addTestimonial = (testimonialData) => {
    return this.post(ENDPOINTS.TESTIMONIALS, testimonialData);
  };

  getTestimonials = (params = {}) => {
    return this.get(ENDPOINTS.TESTIMONIALS, { params });
  };
  getAllTestimonials = (params = {}) => {
    return this.get(`${ENDPOINTS.TESTIMONIALS}all`, { params });
  };

  approveTestimonial = (testimonialId, statusUpdate) => {
    return this.put(`${ENDPOINTS.TESTIMONIALS}${testimonialId}/approve`, statusUpdate);
  };

  getUserTestimonials = (userId) => {
    return this.get(`${ENDPOINTS.TESTIMONIALS}user/${userId}`);
  };

  deleteTestimonial = (testimonialId) => {
    return this.delete(`${ENDPOINTS.TESTIMONIALS}${testimonialId}`);
  };

  // Contact API
  submitContactForm = (formData) => {
    return this.post(ENDPOINTS.CONTACT, formData);
  };

  // Admin API
  getAdminDashboard = () => {
    return this.get('/admin/dashboard');
  };

  getAdminStats = (period = '30d') => {
    return this.get('/admin/stats', { params: { period } });
  };

  getUserReport = (params = {}) => {
    return this.get('/admin/reports/users', { params });
  };

  getAllUsers = (params = {}) => {
    return this.get(ENDPOINTS.USERS, { params });
  };

  updateUser = (userId, userData) => {
    return this.put(`/users/${userId}`, userData);
  };

  deleteUser = (userId) => {
    return this.delete(`/users/${userId}`);
  };

  getAllRegistrations = (params = {}) => {
    return this.get(`${ENDPOINTS.REGISTRATIONS}all/registrations`, { params });
  };

  getRegistrationDetails = (registrationId) => {
    return this.get(`${ENDPOINTS.REGISTRATIONS}details/${registrationId}`);
  };

  getRegistrationsByClass = (classId, params = {}) => {
    return this.get(`${ENDPOINTS.REGISTRATIONS}class/${classId}`, { params });
  };

  getRegistrationsByEvent = (eventId, params = {}) => {
    return this.get(`${ENDPOINTS.REGISTRATIONS}event/${eventId}`, { params });
  };

  createRegistration = async (registrationData, type) => {
    try {
      // Format the payload according to the /register endpoint expectations
      const payload = {
        entity_type: type,
        ...registrationData
      };

      // If it's a class registration, use class_id, otherwise use event_id
      if (type === 'class') {
        payload.class_id = registrationData.class_id || registrationData.event_session_id;
      } else {
        payload.event_id = registrationData.event_session_id;
      }

      // Send the registration data to the register endpoint
      const response = await this.post(`${ENDPOINTS.REGISTRATIONS}register`, payload);

      // Log the response for debugging
      //console.log('Registration API Response:', response);

      // Return the response directly as it's already processed by axios
      return response;
    } catch (error) {
      console.error('Registration API Error:', error);

      // If there's a response with error details, log them
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error status:', error.response.status);
        console.error('Error headers:', error.response.headers);

        // Format a more detailed error message
        if (error.response.data?.detail) {
          const errorDetails = Array.isArray(error.response.data.detail)
            ? error.response.data.detail.map(e => e.msg || e.message).join('; ')
            : error.response.data.detail;

          error.message = `Registration failed: ${errorDetails || 'Unknown error'}`;
        }
      }

      // Re-throw the error to be handled by the component
      throw error;
    }
  };

  getAllInstructor = () => {
    return this.get(`${ENDPOINTS.USERS}`, { params: { role: 'instructor' } });
  };

  getUserRegistrations = async (params = { limit: 50, skip: 0 }) => {

    return this.get(`${ENDPOINTS.REGISTRATIONS}my-registrations`, { params });
  };
}

export const apiService = new ApiService();
export default apiService;
